/**
 * Мои уведомления: тихие часы и подписки финансового агента.
 *
 * Тихие часы общие на три бота (§10.3 ТЗ платформы) — экран говорит это
 * до сохранения. Подписки — только у кого есть вход в финансы: остальным
 * финансовый агент ничего не присылает.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { ErrorState, Loading } from '@shell/states';
import type { Navigation } from '../../api';
import { Notice } from '../../ui';
import { SETTINGS_KEYS, problemText, settingsApi } from './api';
import type { QuietHours, Subscription, Subscriptions } from './api';
import { SetSwitch } from './SetSwitch';

type Result = { ok: boolean; text: string } | null;

export function Notifications({ nav }: { nav: Navigation }) {
  const finance = nav.user.is_host || Boolean(nav.services.finance);
  return (
    <div className="set-stack">
      <Quiet />
      {finance ? (
        <FinanceSubscriptions />
      ) : (
        <section className="set-card">
          <h2 className="set-card__title">Сообщения агентов</h2>
          <p className="set-hint">
            Операционный и обучающий агенты присылают сообщения по делу — о чек-листах, накладных и
            курсах — и отдельной настройки не имеют. Отключить ночные сообщения можно тихими часами.
          </p>
        </section>
      )}
    </div>
  );
}

function Quiet() {
  const client = useQueryClient();
  const [result, setResult] = useState<Result>(null);
  const [from, setFrom] = useState('23:00');
  const [to, setTo] = useState('08:00');
  const query = useQuery<QuietHours, ApiError>({
    queryKey: SETTINGS_KEYS.quiet,
    queryFn: () => settingsApi.quiet(),
  });
  useEffect(() => {
    if (query.data?.enabled) {
      setFrom(query.data.quiet_from ?? '23:00');
      setTo(query.data.quiet_to ?? '08:00');
    }
  }, [query.data]);
  const save = useMutation<QuietHours, ApiError, { enabled: boolean }>({
    mutationFn: ({ enabled }) =>
      settingsApi.setQuiet(enabled ? { enabled, quiet_from: from, quiet_to: to } : { enabled }),
    onSuccess: (data) => {
      client.setQueryData(SETTINGS_KEYS.quiet, data);
      setResult({ ok: true, text: data.message ?? 'Сохранено.' });
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
  });

  return (
    <section className="set-card" aria-label="Тихие часы">
      <h2 className="set-card__title">Тихие часы</h2>
      <p className="set-hint">
        Общие для всех трёх ботов «Тосканы»: обычные сообщения, пришедшие ночью, дождутся утра;
        срочные придут сразу.
      </p>
      {query.isPending ? (
        <Loading />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <>
          <SetSwitch
            label={query.data.enabled ? 'Включены' : 'Выключены'}
            checked={query.data.enabled}
            disabled={save.isPending}
            onChange={(on) => {
              setResult(null);
              save.mutate({ enabled: on });
            }}
          />
          <p className="set-value">{query.data.text}</p>
          <div className="set-inline">
            <label className="field">
              <span className="field__label">С</span>
              <input
                className="input set-time"
                type="time"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
                aria-label="Тихие часы с"
              />
            </label>
            <label className="field">
              <span className="field__label">До</span>
              <input
                className="input set-time"
                type="time"
                value={to}
                onChange={(event) => setTo(event.target.value)}
                aria-label="Тихие часы до"
              />
            </label>
            <button
              type="button"
              className="button button--primary"
              disabled={save.isPending || !from || !to}
              onClick={() => {
                setResult(null);
                save.mutate({ enabled: true });
              }}
            >
              Сохранить время
            </button>
          </div>
        </>
      )}
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
    </section>
  );
}

function FinanceSubscriptions() {
  const query = useQuery<Subscriptions, ApiError>({
    queryKey: SETTINGS_KEYS.subscriptions,
    queryFn: () => settingsApi.subscriptions(),
  });
  return (
    <section className="set-card" aria-label="Финансовый агент">
      <h2 className="set-card__title">Финансовый агент</h2>
      <p className="set-hint">
        Что присылать и когда. Сводки по умолчанию включены, сигналы — выключены: включите те,
        что вам нужны.
      </p>
      {query.isPending ? (
        <Loading />
      ) : query.error ? (
        query.error.isForbidden ? (
          <p className="set-hint">Входа в финансового агента у вас нет — подписок тоже.</p>
        ) : (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        )
      ) : (
        query.data.groups.map((group) => (
          <div className="set-group" key={group.title}>
            <h3 className="block-title">{group.title}</h3>
            <ul className="set-list">
              {group.items.map((item) => (
                <SubscriptionRow key={item.code} item={item} />
              ))}
            </ul>
          </div>
        ))
      )}
    </section>
  );
}

function SubscriptionRow({ item }: { item: Subscription }) {
  const client = useQueryClient();
  const [result, setResult] = useState<Result>(null);
  const [at, setAt] = useState(item.at ?? '');
  useEffect(() => setAt(item.at ?? ''), [item.at]);
  const save = useMutation<Subscriptions, ApiError, { enabled?: boolean; at?: string }>({
    mutationFn: (body) => settingsApi.setSubscription(item.code, body),
    onSuccess: (data, body) => {
      client.setQueryData(SETTINGS_KEYS.subscriptions, data);
      setResult(body.at ? { ok: true, text: `Время: ${body.at}.` } : null);
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
  });

  return (
    <li className="set-row">
      <div className="set-row__main">
        <span className="set-row__title">{item.title}</span>
        <span className="set-row__hint">
          {item.hint}
          {item.weekday_name && item.at && ` · ${item.weekday_name}`}
        </span>
        {!item.working && <span className="set-row__stage">пока не работает — {item.stage}</span>}
      </div>
      <div className="set-row__control">
        {item.working && item.scheduled && item.enabled && (
          <input
            className="input set-time"
            type="time"
            value={at}
            aria-label={`Время: ${item.title}`}
            disabled={save.isPending}
            onChange={(event) => setAt(event.target.value)}
            onBlur={() => {
              if (at && at !== item.at) {
                setResult(null);
                save.mutate({ at });
              }
            }}
          />
        )}
        <SetSwitch
          label={item.title}
          hideLabel
          checked={item.enabled}
          disabled={!item.working || save.isPending}
          onChange={(on) => {
            setResult(null);
            save.mutate({ enabled: on });
          }}
        />
      </div>
      {result && (
        <span className={`set-row__result${result.ok ? '' : ' set-row__result--error'}`}>
          {result.text}
        </span>
      )}
    </li>
  );
}
